import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Phone, Mail, CheckCircle, MessageCircle, ArrowRight, Award, Car,
  MapPin, Clock, Star, ChevronRight, Settings2, Play,
} from 'lucide-react';
import SEOFooter from '@/components/marketing/SEOFooter';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import { useGoogleReviews } from '@/hooks/useGoogleReviews';
import {
  generateEnhancedLocalBusinessSchema,
  generateBreadcrumbSchema,
} from '@/components/marketing/SEOHead';
import studentPass1 from '@/assets/student-pass-1.jpg';
import studentPass2 from '@/assets/student-pass-2.jpg';
import studentPass3 from '@/assets/student-pass-3.jpg';
import ianVideoAsset from '@/assets/ian-intensive-video.mp4.asset.json';
import ianPosterAsset from '@/assets/ian-intensive-poster.jpg.asset.json';
import { PHONE_SMS, PHONE_TEL } from '@/config/business';
import { Button } from '@/components/ui/button';
import StudentAppShowcase from '@/components/marketing/StudentAppShowcase';

const ianVideo = ianVideoAsset.url;
const ianPoster = ianPosterAsset.url;

const sms = (msg: string) => `${PHONE_SMS}?&body=${encodeURIComponent(msg)}`;
const MESSAGE_IAN = sms("Hi Ian, I'd like to ask about driving lessons in Bath.");
const MESSAGE_AVAILABILITY = sms('Hi Ian, please could you let me know your driving lesson availability in Bath?');
const MESSAGE_MANUAL = sms("Hi Ian, I'd like to ask about manual driving lessons in Bath.");
const MESSAGE_AUTOMATIC = sms("Hi Ian, I'd like to ask about automatic driving lessons in Bath.");
const MESSAGE_PRICE = sms('Hi Ian, please could you give me a price for driving lessons in Bath?');

const primaryCta =
  'inline-flex w-full sm:w-auto min-h-[52px] items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-[16px] bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 text-[#06121a] shadow-[0_12px_34px_-10px_rgba(45,212,191,0.65)] hover:-translate-y-0.5 transition-all';

const heroBadges = [
  { icon: Award, label: 'DVSA Grade A' },
  { icon: Clock, label: "25+ Years' Experience" },
];

const included = [
  'One-to-one tuition',
  'Door-to-door pickup across Bath',
  'DVSA Grade A instructor',
  'Your student progress app',
  'Test preparation',
  'Mock driving tests',
];

const steps = [
  { t: 'Message Ian', d: "Tell Ian whether you're looking for manual or automatic lessons and your current driving experience." },
  { t: 'Book your lesson', d: 'Choose an available lesson time. Lessons are booked in minimum 2-hour sessions.' },
  { t: 'Start learning', d: 'One-to-one tuition with structured progress towards your driving test.' },
];

const faqs = [
  {
    q: 'How much are driving lessons in Bath?',
    a: 'Text Ian directly for a price for your driving lessons. Lessons are booked as 2-hour sessions.',
  },
  {
    q: 'Do you offer manual driving lessons in Bath?',
    a: 'Yes. Manual lessons are taught personally by Ian in a dual-control car. Text Ian directly for a price.',
  },
  {
    q: 'Do you offer automatic driving lessons in Bath?',
    a: 'Yes. Automatic lessons are available. Text Ian directly for a price.',
  },
  {
    q: 'How long is a driving lesson?',
    a: 'Lessons are booked in blocks of at least two hours, giving enough time to make real progress.',
  },
  {
    q: 'Do you teach complete beginners?',
    a: 'Yes. Beginners start with car controls in quieter areas before moving on to busier roads.',
  },
  {
    q: 'Do you provide door-to-door pickup in Bath?',
    a: 'Yes. Lessons start and finish at your home, work or college across Bath, including BA1 and BA2 postcodes.',
  },
  {
    q: 'Can you help me prepare for my driving test?',
    a: 'Yes. Lessons include test preparation and mock tests on the routes used at the Chippenham and Kingswood test centres. Bath itself has no test centre.',
  },
  {
    q: 'Do you offer intensive driving courses?',
    a: 'Yes. Intensive manual and automatic courses are available as a separate service if you already have a test booked.',
  },
  {
    q: 'Which areas do you cover?',
    a: 'Lessons are Bath-based, covering BA1 and BA2 postcodes and nearby areas, with test preparation around Chippenham and Kingswood.',
  },
];

const localBusinessSchema = generateEnhancedLocalBusinessSchema();
const breadcrumbSchema = generateBreadcrumbSchema([{ name: 'Home', url: 'https://best-drive.co.uk' }]);
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};
const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://best-drive.co.uk/#website',
  url: 'https://best-drive.co.uk',
  name: 'Best-Drive Bath',
  publisher: { '@id': 'https://best-drive.co.uk/#localbusiness' },
};
const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Driving Lessons in Bath',
  serviceType: 'Manual and automatic driving lessons',
  description:
    'Manual and automatic driving lessons in Bath with DVSA Grade A instructor Ian Perry. Text Ian directly for a price.',
  provider: { '@id': 'https://best-drive.co.uk/#localbusiness' },
  areaServed: [
    { '@type': 'City', name: 'Bath' },
    { '@type': 'City', name: 'Chippenham' },
    { '@type': 'City', name: 'Kingswood' },
  ],
};

const testimonials = [
  { image: studentPass1, name: 'Andy' },
  { image: studentPass2, name: 'Masha' },
  { image: studentPass3, name: 'Marcella' },
];

const LessonCard: React.FC<{ title: string; note: string; ctaLabel: string; href: string; primary?: boolean }> = ({ title, note, ctaLabel, href, primary = false }) => (
  <div className={`rounded-lg border p-6 flex flex-col ${primary ? 'border-primary/50 bg-card' : 'border-border bg-background'}`}>
    <div className="flex items-center gap-3 mb-3">
      <Settings2 className="h-5 w-5 text-primary shrink-0" />
      <h3 className="text-lg sm:text-xl font-bold text-foreground leading-tight">{title}</h3>
    </div>
    <p className="text-sm text-muted-foreground leading-relaxed">{note}</p>
    <Button asChild variant="link" className="mt-4 p-0 justify-start h-auto whitespace-normal text-primary">
      <a href={href}>{ctaLabel}<ArrowRight className="h-4 w-4 shrink-0" /></a>
    </Button>
  </div>
);

const DrivingLessonsBath: React.FC = () => {
  const [videoPlaying, setVideoPlaying] = React.useState(false);
  const { data: googleReviews } = useGoogleReviews();
  const verifiedReviews = googleReviews.source !== 'empty' ? googleReviews.reviews.filter(review => review.text && review.google_uri).slice(0, 2) : [];
  const verifiedRating = googleReviews.source !== 'empty' && googleReviews.user_rating_count && googleReviews.rating;


  // Homepage is the single canonical home for driving lessons in Bath.
  const canonicalUrl = 'https://best-drive.co.uk/';
  const pageTitle = 'Driving Lessons Bath | Manual & Automatic | Best Drive';
  const pageDescription =
    'Manual and automatic driving lessons in Bath with DVSA Grade A instructor Ian Perry. Text Ian directly for a price.';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="geo.region" content="GB-SOM" />
        <meta name="geo.placename" content="Bath" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en-gb" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://best-drive.co.uk/og-image.jpg?v=2" />
        <meta property="og:image:width" content="1500" />
        <meta property="og:image:height" content="1000" />
        <meta property="og:image:alt" content="Best Drive driving school car in Bath" />
        <meta property="og:site_name" content="Best-Drive Bath" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://best-drive.co.uk/og-image.jpg?v=2" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen w-full overflow-x-hidden bg-background text-foreground">
        <MarketingHeader showBookOnline />

        <main className="pb-12">
          {/* ===== HERO ===== */}
          <section className="relative overflow-hidden bg-background">
            <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 pt-[72px] pb-8 sm:pt-12 sm:pb-14 text-center">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10px] sm:text-[11px] font-semibold tracking-[0.16em] uppercase mb-3.5 bg-primary/10 border border-primary/30 text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Manual driving lessons — Bath
              </span>

              <h1 className="text-[32px] leading-[1.06] sm:text-5xl lg:text-6xl font-bold tracking-normal mb-2.5">
                Driving Lessons in{' '}
                <span className="text-gradient-premium">
                  Bath
                </span>
              </h1>

              <p className="text-sm sm:text-base !font-normal mb-5 max-w-xl mx-auto leading-relaxed">
                Manual, one-to-one lessons with me, Ian Perry — DVSA Grade A, with 25+ years’ experience. Automatic lessons also available.
              </p>


              {/* Personal enquiries stay prominent; booking lives in the header. */}
              <Button asChild size="lg" className="btn-premium w-full sm:w-auto min-h-[52px] rounded-full px-6 text-base">
                <a href={MESSAGE_IAN}>
                  <MessageCircle className="h-4 w-4" />
                  Message Ian
                </a>
              </Button>
              <p className="mt-2 text-[12.5px] text-foreground">Usually replies personally the same day.</p>

              {/* Only show a Google rating returned by the existing review service. */}
              {verifiedRating ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  <a href={googleReviews.google_maps_uri || 'https://maps.google.com/?cid=14526900073285771924'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 underline underline-offset-4">
                    <Star className="h-4 w-4 text-excellence" />
                    {googleReviews.rating} / 5 from {googleReviews.user_rating_count} Google reviews
                  </a>
                </p>
              ) : null}

              {/* BADGES — wrap, never overflow */}
              <ul className="mt-2 grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-1.5 sm:gap-2">
                {heroBadges.map(({ icon: Icon, label }, i) => (
                  <li
                    key={label}
                    className={`flex items-center justify-center gap-1 px-1.5 py-1 rounded-full border border-border bg-card ${
                      i === heroBadges.length - 1 && heroBadges.length % 2 === 1 ? 'col-span-2 justify-self-center px-3 sm:col-span-1' : ''
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="text-[11px] min-[360px]:text-xs font-medium text-foreground whitespace-nowrap">{label}</span>
                  </li>
                ))}
              </ul>

            </div>
          </section>

          {verifiedReviews.length > 0 && (
            <section aria-label="Google reviews" className="px-5 py-6 border-y border-border">
              <div className="mx-auto max-w-5xl grid sm:grid-cols-2 gap-6">
                {verifiedReviews.map(review => (
                  <blockquote key={review.id} className="text-sm leading-relaxed text-muted-foreground">
                    <p className="line-clamp-3">“{review.text}”</p>
                    <cite className="mt-2 block not-italic text-foreground">
                      {review.author} · <a href={review.google_uri} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 text-primary">Google review</a>
                    </cite>
                  </blockquote>
                ))}
              </div>
            </section>
          )}

          <section aria-labelledby="personal-tuition" className="px-5 py-6 sm:py-12">
            <div className="mx-auto max-w-3xl text-center">
              <h2 id="personal-tuition" className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Your instructor, every lesson</h2>
              <p className="text-base text-muted-foreground leading-relaxed">I personally teach every learner — you won’t be assigned a different instructor. I specialise in helping nervous and anxious learners, and adapt my teaching for ADHD and dyspraxia, with clear explanations and a pace that suits you.</p>
            </div>
          </section>

          {/* ===== DRIVING LESSONS ===== */}
          <section id="driving-lessons" className="py-10 sm:py-16 px-5 scroll-mt-20 bg-background">
            <div className="container mx-auto max-w-5xl">
              <div className="text-center mb-6">
                <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-primary mb-2">Driving lessons in Bath</p>
                <h2 className="text-[26px] sm:text-4xl font-bold leading-[1.12] tracking-normal mb-2.5">
                  Learn to drive a manual car
                </h2>
                <p className="text-[15px] text-foreground max-w-xl mx-auto leading-relaxed">
                  One-to-one lessons tailored to your experience, confidence and progress.
                </p>
              </div>

              <div className="grid md:grid-cols-[1.2fr_1fr] gap-6">
                <LessonCard
                  primary
                  title="Manual Driving Lessons"
                  note="Learn in a dual-control manual car. A manual licence allows you to drive both manual and automatic cars."
                  ctaLabel="Ask about manual lessons"
                  href={MESSAGE_MANUAL}
                />
                <LessonCard
                  title="Automatic Driving Lessons"
                  note="No clutch or manual gear changes, allowing you to concentrate on observation, positioning and building confidence."
                  ctaLabel="Ask about automatic lessons"
                  href={MESSAGE_AUTOMATIC}
                />
              </div>
            </div>
          </section>

          {/* ===== WHAT'S INCLUDED ===== */}
          <section id="whats-included" className="py-9 sm:py-14 px-5 scroll-mt-20">
            <div className="container mx-auto max-w-3xl">
              <div className="text-center mb-5">
                <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-primary mb-2">What's included</p>
                <h2 className="text-[26px] sm:text-4xl font-bold leading-[1.12] tracking-normal">Every lesson includes</h2>
              </div>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {included.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 rounded-lg border border-border bg-card px-4 py-2.5 text-[14.5px] text-foreground">
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <StudentAppShowcase />

          <section id="intensive-courses" className="px-5 py-6 scroll-mt-20">
            <div className="mx-auto max-w-3xl text-center text-sm text-muted-foreground">
              Looking for a concentrated course instead?{' '}
              <Link to="/intensive-driving-courses" className="text-primary underline underline-offset-4">View intensive driving courses<ArrowRight className="inline ml-1 h-4 w-4" /></Link>
            </div>
          </section>

          {/* ===== PRICE ENQUIRY ===== */}
          <section id="prices" className="py-9 sm:py-14 px-5 scroll-mt-20">
            <div className="container mx-auto max-w-md text-center">
              <Button asChild size="lg" className="btn-premium w-full sm:w-auto rounded-full min-h-[52px]">
                <a href={MESSAGE_PRICE} data-direct-sms="true">
                  <MessageCircle className="h-4 w-4" />
                  Ask now for a price
                </a>
              </Button>
            </div>
          </section>

          {/* ===== YOUR DRIVING INSTRUCTOR ===== */}
          <section id="your-instructor" className="py-10 sm:py-16 px-5 scroll-mt-20">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-5">
                <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-primary mb-2">Meet your instructor</p>
                <h2 className="text-[26px] sm:text-4xl font-bold leading-[1.12] tracking-normal">Ian Perry</h2>
                <p className="mt-1 text-sm font-semibold text-primary">DVSA Grade A Driving Instructor</p>
              </div>

              <div className="relative max-w-xl mx-auto">
                <div
                  aria-hidden="true"
                  className="absolute -inset-2 rounded-[28px] opacity-40 blur-2xl"
                  style={{ background: 'linear-gradient(135deg, rgba(45,212,191,0.22), rgba(56,189,248,0.16))' }}
                />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#06101c] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]">
                  <div className="aspect-video relative">
                    <video
                      className="h-full w-full object-cover"
                      controls
                      preload="none"
                      playsInline
                      poster={ianPoster}
                      aria-label="Meet Ian Perry — DVSA Grade A driving instructor in Bath"
                      onPlay={() => setVideoPlaying(true)}
                      onPause={() => setVideoPlaying(false)}
                      onEnded={() => setVideoPlaying(false)}
                    >
                      <source src={ianVideo} type="video/mp4" />
                      <track kind="captions" srcLang="en" label="English" />
                      Your browser does not support embedded video.
                    </video>
                    {!videoPlaying && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <span className="h-14 w-14 rounded-full bg-white/95 shadow-[0_10px_30px_rgba(0,0,0,0.4)] flex items-center justify-center">
                          <Play className="h-6 w-6 text-[#06121a] ml-1" fill="#06121a" />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-5 text-center max-w-xl mx-auto">
                <p className="text-[15px] text-foreground leading-relaxed">
                  Every lesson is taught personally by Ian. With 25+ years' experience, you'll have the same
                  instructor throughout your lessons and test preparation.
                </p>
              </div>

              <div className="mt-5 text-center">
                <a href={MESSAGE_IAN} className="inline-flex items-center gap-2 text-primary hover:text-primary text-sm font-semibold">
                  Message Ian
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>

              <div className="mt-3 text-center">
                <Link to="/about-ian" className="inline-flex items-center gap-2 text-foreground hover:text-foreground text-[13px]">
                  More about Ian Perry, driving instructor in Bath
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </section>

          {/* A verified dated sample is needed before publishing a numerical pass rate. */}
          <section id="pass-rate" className="px-5 pb-12 scroll-mt-20">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-xl font-semibold text-foreground mb-3">Preparation, not promises</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">I’ll help you build the skills and confidence for your test. Results vary and rates can rise or fall.</p>
            </div>
          </section>

          {/* ===== HOW IT WORKS ===== */}
          <section id="how-it-works" className="py-10 sm:py-16 px-5 scroll-mt-20 bg-background">
            <div className="container mx-auto max-w-2xl">
              <div className="text-center mb-7">
                <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-primary mb-2">How it works</p>
                <h2 className="text-[26px] sm:text-4xl font-bold leading-[1.12] tracking-normal">How driving lessons work</h2>
              </div>

              <ol className="relative pl-12 space-y-5 before:absolute before:left-[18px] before:top-3 before:bottom-3 before:w-px before:bg-gradient-to-b before:from-teal-400/60 before:via-teal-400/20 before:to-transparent">
                {steps.map((step, i) => (
                  <li key={step.t} className="relative">
                    <span className="absolute -left-12 top-0 h-9 w-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-[#06121a] font-bold flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(20,184,166,0.6)]">
                      {i + 1}
                    </span>
                    <h3 className="text-base sm:text-lg font-semibold text-foreground">{step.t}</h3>
                    <p className="mt-0.5 text-[14.5px] text-foreground leading-relaxed">{step.d}</p>
                  </li>
                ))}
              </ol>

              <div className="mt-6 text-center">
                <a href={MESSAGE_AVAILABILITY} className="inline-flex items-center gap-2 text-primary hover:text-primary text-sm font-semibold">
                  Check lesson availability
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </section>

          {/* ===== PROOF ===== */}
          <section id="reviews" className="scroll-mt-20 py-10 sm:py-16 px-5 bg-card">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-5">
                <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-primary mb-2">Learner passes</p>
                <h2 className="text-[26px] sm:text-4xl font-bold leading-[1.12] tracking-normal">Real students. Real passes.</h2>
              </div>

              <div className="-mx-5 px-5 overflow-x-auto snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex gap-4 pb-2">
                  {testimonials.map((t) => (
                    <figure
                      key={t.name}
                      className="snap-start shrink-0 w-[74%] sm:w-[44%] lg:w-[31%] rounded-lg overflow-hidden bg-card border border-border"
                    >
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img src={t.image} alt={`${t.name} passed their driving test with Best-Drive Bath`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220] via-[#0b1220]/30 to-transparent" />
                      </div>
                      <figcaption className="p-4">
                        <p className="text-sm font-semibold text-foreground">{t.name}</p>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ===== LOCAL AREA ===== */}
          <section id="areas" className="py-10 sm:py-16 px-5 scroll-mt-20">
            <div className="container mx-auto max-w-3xl">
              <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-primary mb-2">
                <MapPin className="inline h-3 w-3 mr-1" /> Areas covered
              </p>
              <h2 className="text-[26px] sm:text-4xl font-bold leading-[1.12] tracking-normal mb-4">Where Ian teaches</h2>
              <div className="space-y-3.5 text-[15px] text-foreground leading-relaxed">
                <p>
                  Lessons are Bath-based with door-to-door pickup across the city, including BA1 and BA2 postcodes.
                  Beginners start with car controls on quieter streets and build up to busier roads at their own pace.
                </p>
                <p>
                  Bath has no DVSA test centre, so test practice covers the routes used at Chippenham and Kingswood,
                  and lessons are also available for learners travelling from those areas. If you need to train over a
                  shorter period,{' '}
                  <Link to="/intensive-driving-courses-bath" className="text-primary hover:text-primary font-semibold underline underline-offset-4">
                    intensive driving courses in Bath
                  </Link>{' '}
                  are available as a separate service.
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mt-6">
                {[
                  { name: 'Bath', note: 'Home base · door-to-door pickup (BA1 & BA2).' },
                  { name: 'Chippenham', note: 'Test centre · rural and dual-carriageway routes.' },
                  { name: 'Kingswood', note: 'Test centre · Bristol-side, roundabout-heavy routes.' },
                ].map((a) => (
                  <div key={a.name} className="rounded-lg border border-border bg-card  p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="h-6 w-6 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                        <MapPin className="h-3 w-3 text-primary" />
                      </span>
                      <p className="text-base font-semibold text-foreground">{a.name}</p>
                    </div>
                    <p className="text-[13px] text-foreground leading-relaxed">{a.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== FAQs ===== */}
          <section id="faqs" className="py-10 sm:py-16 px-5 scroll-mt-20">
            <div className="container mx-auto max-w-3xl">
              <div className="text-center mb-6">
                <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-primary mb-2">FAQs</p>
                <h2 className="text-[26px] sm:text-4xl font-bold leading-[1.12] tracking-normal">Frequently Asked Questions</h2>
              </div>

              <div className="space-y-2.5">
                {faqs.map((f) => (
                  <details key={f.q} className="group rounded-lg border border-border bg-card  px-4 py-3.5 open:border-primary/30 transition-colors">
                    <summary className="flex items-start justify-between gap-3 cursor-pointer list-none">
                      <h3 className="text-[15px] font-semibold text-foreground leading-snug">{f.q}</h3>
                      <span className="shrink-0 h-6 w-6 rounded-full border border-border flex items-center justify-center text-foreground group-open:rotate-45 transition-transform text-base leading-none">+</span>
                    </summary>
                    <p className="mt-2.5 text-[14px] text-foreground leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ===== FINAL CTA ===== */}
          <section id="contact" className="relative py-12 sm:py-16 px-5 scroll-mt-20 overflow-hidden">
            <div className="container mx-auto max-w-md text-center relative z-10">
              <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-primary mb-2">Ready to start?</p>
              <h2 className="text-[26px] sm:text-4xl font-bold leading-[1.12] tracking-normal mb-3">Driving Lessons in Bath</h2>
              <p className="text-[15px] text-foreground mb-6">Manual or automatic lessons with Ian.</p>

              <a href={MESSAGE_AVAILABILITY} aria-label="Message Ian about lesson availability" className={primaryCta}>
                <MessageCircle className="h-5 w-5" />
                Message Ian about availability
              </a>
              <p className="mt-2 text-[12.5px] text-foreground">Personal reply from Ian — no call centre.</p>

              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-foreground text-sm">
                <a href={PHONE_TEL} className="flex items-center gap-2 hover:text-foreground transition-colors">
                  <Phone className="h-4 w-4" />
                  <span className="font-medium">07564 936033</span>
                </a>
                <span className="hidden sm:inline text-foreground">·</span>
                <a href="mailto:help@best-drive.co.uk" className="flex items-center gap-2 hover:text-foreground transition-colors">
                  <Mail className="h-4 w-4" />
                  <span className="font-medium">help@best-drive.co.uk</span>
                </a>
              </div>
            </div>
          </section>

          {/* ===== RELATED PAGES ===== */}
          <section className="py-10 px-5 border-t border-border">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-5 text-center">More from Best-Drive</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Link to="/areas/bath" className="rounded-lg border border-border bg-card p-4 hover:bg-card transition-all">
                  <p className="font-semibold text-foreground mb-1">Driving lessons in Bath</p>
                  <p className="text-[13px] text-foreground">Local area guide, pickup zones and test routes.</p>
                </Link>
                <Link to="/intensive-driving-courses-bath" className="rounded-lg border border-border bg-card p-4 hover:bg-card transition-all">
                  <p className="font-semibold text-foreground mb-1">Intensive driving courses in Bath</p>
                  <p className="text-[13px] text-foreground">Manual and automatic courses around your test date.</p>
                </Link>
                <Link to="/prices" className="rounded-lg border border-border bg-card p-4 hover:bg-card transition-all">
                  <p className="font-semibold text-foreground mb-1">All prices</p>
                  <p className="text-[13px] text-foreground">Lessons, intensive courses and test preparation.</p>
                </Link>
                <Link to="/before-your-test" className="rounded-lg border border-border bg-card p-4 hover:bg-card transition-all">
                  <p className="font-semibold text-foreground mb-1">Before your driving test</p>
                  <p className="text-[13px] text-foreground">Chippenham and Kingswood routes and what to expect.</p>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <SEOFooter showCookieSettings />
      </div>
    </>
  );
};

export default DrivingLessonsBath;
